import { PayloadAction } from "@reduxjs/toolkit";
import { createSearchParams } from "react-router-dom";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { AppState } from "../../../store";
import fetchAPI, {
  ApiResponse,
  HttpMethod,
  Pagination,
} from "../../../utils/api";
import { showSuccess } from "../../toaster/store/slice";
import { User } from "../../users/store/slice";
import userPageSlice from "./slice";
import { UnassignBookingsValues } from "../../unassign-modal/UnassignModal";

type UserListResponse = ApiResponse<{ data: User[]; meta: Pagination }>;
type UserResponse = ApiResponse<{ data: User[] }>;
type UserDeleteResponse = ApiResponse<{ success: true }>;
type UserRestoreResponse = ApiResponse<{ success: true }>;
type UserInviteRetryResponse = ApiResponse<{ success: true }>;
type UnassignBookingsResponse = ApiResponse<{ success: true }>;

function* userPageSaga() {
  yield takeLatest(userPageSlice.actions.setSearch, handleDebounce);
  yield takeLatest(userPageSlice.actions.loadMore, handleLoadMore);
  yield takeLatest(userPageSlice.actions.create, handleCreate);
  yield takeLatest(userPageSlice.actions.update, handleUpdate);
  yield takeLatest(userPageSlice.actions.remove, handleRemove);
  yield takeLatest(userPageSlice.actions.restore, handleRestore);
  yield takeLatest(userPageSlice.actions.retryInvite, handleRetryInvite);
  yield takeLatest(userPageSlice.actions.unassignBookings, handleUnassign);
}

function* handleDebounce() {
  yield delay(300);

  yield call(handleFetch);
}

function* handleFetch() {
  const search: string = yield select(
    (state: AppState) => state.userPage.search
  );
  const meta: Pagination | null = yield select(
    (state: AppState) => state.userPage.meta
  );
  const page = meta && meta.current_page ? meta.current_page : 1;

  const params = createSearchParams({
    page: page.toString(),
    per_page: "10",
    search,
  }).toString();

  const response: UserListResponse = yield call(
    fetchAPI,
    `/users?include_deleted=1&with_bookings_count=1&${params}`
  );

  if (response && response.data && response.meta) {
    yield put(userPageSlice.actions.setData(response));
  }

  yield put(userPageSlice.actions.setIsLoading(false));
}

function* handleLoadMore() {
  const meta: Pagination | null = yield select(
    (state: AppState) => state.userPage.meta
  );
  const page = meta && meta.current_page ? meta.current_page : 1;

  const params = createSearchParams({
    page: page.toString(),
    per_page: "10",
  }).toString();

  const response: UserListResponse = yield call(fetchAPI, `/users?${params}`);

  if (response && response.data && response.meta) {
    yield put(userPageSlice.actions.addData(response));
  }

  yield put(userPageSlice.actions.setIsLoading(false));
}

function* handleCreate({ payload: company }: PayloadAction<User>) {
  const response: UserResponse = yield call(fetchAPI, `/users`, {
    method: HttpMethod.POST,
    body: JSON.stringify(company),
  });

  if (response && response.data) {
    yield put(userPageSlice.actions.setEditableIndex(undefined));
    yield put(userPageSlice.actions.setSearch(""));
    yield put(showSuccess("User created."));
  }

  yield put(userPageSlice.actions.setIsCreating(false));
}

function* handleUpdate({ payload: company }: PayloadAction<User>) {
  const response: UserResponse = yield call(fetchAPI, `/users/${company.id}`, {
    method: HttpMethod.PUT,
    body: JSON.stringify(company),
  });

  if (response && response.data) {
    yield put(userPageSlice.actions.setEditableIndex(undefined));
    yield put(userPageSlice.actions.updateUser(response.data));
    yield put(showSuccess("User updated."));
  }

  yield put(userPageSlice.actions.setIsUpdating(false));
}

function* handleRemove({ payload: user }: PayloadAction<User>) {
  const response: UserDeleteResponse = yield call(
    fetchAPI,
    `/users/${user.id}`,
    {
      method: HttpMethod.DELETE,
    }
  );

  if (response && response.success) {
    yield put(userPageSlice.actions.setEditableIndex(undefined));
    yield put(userPageSlice.actions.setSearch(""));
    yield put(showSuccess("User removed."));
  }

  yield put(userPageSlice.actions.setIsRemoving(false));
}

function* handleRestore({ payload: user }: PayloadAction<User>) {
  const response: UserRestoreResponse = yield call(
    fetchAPI,
    `/users/${user.id}/restore`,
    {
      method: HttpMethod.POST,
    }
  );

  if (response && response.success) {
    yield put(userPageSlice.actions.setEditableIndex(undefined));
    yield put(userPageSlice.actions.setSearch(""));
    yield put(showSuccess("User restored."));
  }

  yield put(userPageSlice.actions.setIsRestoring(false));
}

function* handleRetryInvite({ payload: userId }: PayloadAction<number>) {
  const response: UserInviteRetryResponse = yield call(
    fetchAPI,
    `/invitation/${userId}/retry`,
    {
      method: HttpMethod.POST,
    }
  );

  if (response && response.success) {
    yield put(showSuccess("User invitation sent."));
  }

  yield put(userPageSlice.actions.setRetryingUserId(null));
}

function* handleUnassign({
  payload: { fromUser, toUser },
}: PayloadAction<UnassignBookingsValues>) {
  const response: UnassignBookingsResponse = yield call(
    fetchAPI,
    `/users/${fromUser.id}/unassign-bookings/${toUser.id}`,
    {
      method: HttpMethod.POST,
    }
  );

  if (response && response.success) {
    yield put(userPageSlice.actions.setSearch(""));
    yield put(showSuccess(`Bookings assigned to ${toUser.name}.`));
  }

  yield put(userPageSlice.actions.setRetryingUserId(null));
}

export default userPageSaga;
